import { Component, OnInit } from '@angular/core';
import { TeamCards } from '@app/shared/constants/global.enum';
import { TeamManagementCardsResponse } from '@app/shared/interfaces/team-management.model';
import { TeamManagementService } from '../team-management.service';
import { AuthService } from '@app/core/auth.service';
import { excludedCities, locations, QUICK_LINKS_LEFT, QUICK_LINKS_RIGHT, QuickLink } from './team-management-cards.config';
import { Subject, takeUntil } from 'rxjs';

interface DonutSegment {
  color: string;
  dashArray: string;
  dashOffset: number;
}

interface BreakdownItem {
  label: string;
  value: number;
  color: string;
}

interface DonutView {
  segments: DonutSegment[];
  centerValue: string;
  centerLabel: string;
  items: BreakdownItem[];
  empty: boolean;
}

const DONUT_RADIUS = 28;
const DONUT_CIRC = 2 * Math.PI * DONUT_RADIUS;

const PALETTE = {
  green: '#003F2D',
  orange: '#d97706',
  blue: '#2563eb',
  teal: '#0d9488',
  purple: '#7c3aed',
  empty: '#e2e6eb',
};

const DEMO_COLOR_CYCLE = [PALETTE.green, PALETTE.blue, PALETTE.orange, PALETTE.purple, PALETTE.teal];

@Component({
  selector: 'app-team-management-cards',
  templateUrl: './team-management-cards.component.html',
  styleUrl: './team-management-cards.component.scss',
})
export class TeamManagementCardsComponent implements OnInit {
  displayData: TeamManagementCardsResponse | null = null;
  teamCards = TeamCards;
  locations = locations;
  excludedCities = excludedCities;
  quickLinksModalOpened = false;
  private destroy$ = new Subject<void>();

  quickLinksLeft: QuickLink[] = QUICK_LINKS_LEFT;
  quickLinksRight: QuickLink[] = QUICK_LINKS_RIGHT;

  teamOverviewDonut: DonutView = this.emptyDonut();
  employeeTypeDonut: DonutView = this.emptyDonut();
  demographicsDonut: DonutView = this.emptyDonut();

  totalQuickLinks = QUICK_LINKS_LEFT.length + QUICK_LINKS_RIGHT.length;

  constructor(
    private teamCardService: TeamManagementService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
  const employeeId = this.authService.userProfile?.employeeId.toString()!;


  // Subscribe to dropdown changes
  this.teamCardService.getSelectedDropdownValue()
    .pipe(takeUntil(this.destroy$))
    .subscribe((selection) => {
      if (selection) {
        // Fetch data with the selected employee's ID and filterType from dropdown
        this.getTeamCards(selection.employeeId, selection.filterType);
      }
    });
}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTeamCards(employeeId: string, filterType?: string) {
    this.teamCardService.getTeamCardsData(employeeId, filterType).subscribe((res) => {
      this.displayData = Array.isArray(res) ? res[0] : res;
      this.rebuildDonuts();
    });
  }

  getOtherLocationsTotal(): number {
    const demographics = this.displayData?.teamDemographics;
    if (!demographics) return 0;

    return Object.keys(demographics)
      .filter(location => !this.excludedCities.includes(location.toLowerCase()))
      .reduce((sum, location) => sum + (demographics[location] || 0), 0);
  }

  getCityDemographicValue(city: { label: string; value: string }): number {
    if (city.value === 'Others') {
      return this.getOtherLocationsTotal();
    }
    return this.displayData?.teamDemographics?.[city.value] || 0;
  }

  private rebuildDonuts(): void {
    this.teamOverviewDonut = this.buildTeamOverviewDonut();
    this.employeeTypeDonut = this.buildEmployeeTypeDonut();
    this.demographicsDonut = this.buildDemographicsDonut();
  }

  private buildTeamOverviewDonut(): DonutView {
    const teamSize = this.displayData?.teamOverview?.teamSize ?? 0;
    const direct = this.displayData?.teamOverview?.directReportees ?? 0;
    const indirect = Math.max(0, teamSize - direct);
    const items: BreakdownItem[] = [
      { label: this.teamCards.directReportees, value: direct, color: PALETTE.green },
      { label: 'Indirect', value: indirect, color: PALETTE.blue },
    ];
    return {
      segments: this.computeSegments([direct, indirect], [PALETTE.green, PALETTE.blue]),
      centerValue: String(teamSize),
      centerLabel: 'TEAM',
      items: items.filter((it) => it.value > 0 || teamSize === 0),
      empty: teamSize === 0,
    };
  }

  private buildEmployeeTypeDonut(): DonutView {
    const fullTime = this.displayData?.employeeType?.fullTime ?? 0;
    const contractors = this.displayData?.employeeType?.contractors ?? 0;
    const total = fullTime + contractors;
    return {
      segments: this.computeSegments([fullTime, contractors], [PALETTE.green, PALETTE.orange]),
      centerValue: String(total),
      centerLabel: 'TOTAL',
      items: [
        { label: this.teamCards.fullTime, value: fullTime, color: PALETTE.green },
        { label: this.teamCards.contractors, value: contractors, color: PALETTE.orange },
      ],
      empty: total === 0,
    };
  }

  private buildDemographicsDonut(): DonutView {
    const items: BreakdownItem[] = [];
    const values: number[] = [];
    const colors: string[] = [];
    let total = 0;
    this.locations.forEach((city, idx) => {
      const value = this.getCityDemographicValue(city);
      if (value <= 0) return;
      const color = DEMO_COLOR_CYCLE[idx % DEMO_COLOR_CYCLE.length];
      items.push({ label: city.label, value, color });
      values.push(value);
      colors.push(color);
      total += value;
    });
    return {
      segments: this.computeSegments(values, colors),
      centerValue: String(total),
      centerLabel: 'TOTAL',
      items,
      empty: total === 0,
    };
  }

  private computeSegments(values: number[], colors: string[]): DonutSegment[] {
    const total = values.reduce((sum, v) => sum + v, 0);
    if (total <= 0) return [];
    const segments: DonutSegment[] = [];
    let acc = 0;
    values.forEach((value, idx) => {
      if (value <= 0) return;
      const arcLength = (value / total) * DONUT_CIRC;
      segments.push({
        color: colors[idx],
        dashArray: `${arcLength} ${DONUT_CIRC - arcLength}`,
        dashOffset: -acc,
      });
      acc += arcLength;
    });
    return segments;
  }

  private emptyDonut(): DonutView {
    return { segments: [], centerValue: '0', centerLabel: '', items: [], empty: true };
  }
}
